import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, GitBranch } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";
import { ERP_APPROVER_ROLES } from "@/lib/roles";

export default function ApprovalWorkflows() {
  const { isOrgAdmin } = useAuth();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/workflows");
      setWorkflows(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (workflow) => {
    try {
      await api.patch(`/workflows/${workflow.id}`, { is_active: !workflow.is_active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const remove = async (workflow) => {
    try {
      await api.delete(`/workflows/${workflow.id}`);
      toast.success("Workflow deleted");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="approval-workflows-page">
      <PageHeader
        title="Approval Workflows"
        description="Multi-step sequential approval chains, keyed by document type. Documents from later Packs will submit into these."
        action={isOrgAdmin && <CreateWorkflowDialog onCreated={load} />}
      />
      {!loading && workflows.length === 0 ? (
        <EmptyState title="No approval workflows yet" description="Define your first sequential approval chain (e.g. PO → Procurement → Accounts)." testId="workflows-empty-state" />
      ) : (
        <div className="space-y-4">
          {workflows.map((w) => (
            <div key={w.id} className="rounded-xl border border-border bg-card p-4" data-testid={`workflow-card-${w.doc_type}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GitBranch className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">{w.name}</p>
                    <p className="font-mono text-xs text-muted-foreground">doc_type: {w.doc_type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Switch checked={w.is_active} onCheckedChange={() => toggleActive(w)} data-testid={`workflow-active-toggle-${w.doc_type}`} />
                    <Badge variant={w.is_active ? "default" : "secondary"}>{w.is_active ? "Active" : "Inactive"}</Badge>
                  </div>
                  {isOrgAdmin && (
                    <Button size="sm" variant="ghost" onClick={() => remove(w)} data-testid={`delete-workflow-button-${w.doc_type}`}>
                      <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                    </Button>
                  )}
                </div>
              </div>
              <ol className="mt-3 flex flex-wrap items-center gap-2" data-testid={`workflow-steps-${w.doc_type}`}>
                {w.steps.map((s, idx) => (
                  <li key={s.id} className="flex items-center gap-2">
                    <span className="rounded-md border border-border bg-background px-2.5 py-1 text-xs text-foreground">
                      <span className="font-mono text-muted-foreground">{s.step_order}.</span> {s.name} <span className="text-muted-foreground">({s.approver_role})</span>
                    </span>
                    {idx < w.steps.length - 1 && <span className="text-muted-foreground">→</span>}
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateWorkflowDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ doc_type: "", name: "" });
  const [steps, setSteps] = useState([{ name: "", approver_role: ERP_APPROVER_ROLES[0] }]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const addStep = () => setSteps([...steps, { name: "", approver_role: ERP_APPROVER_ROLES[0] }]);
  const removeStep = (idx) => setSteps(steps.filter((_, i) => i !== idx));
  const updateStep = (idx, field, value) => setSteps(steps.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      await api.post("/workflows", {
        ...form,
        steps: steps.map((s, idx) => ({ step_order: idx + 1, name: s.name, approver_role: s.approver_role })),
      });
      toast.success(`Workflow ${form.doc_type} created`);
      setForm({ doc_type: "", name: "" });
      setSteps([{ name: "", approver_role: ERP_APPROVER_ROLES[0] }]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-workflow-button"><Plus className="h-4 w-4" /> New Workflow</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Approval Workflow</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-workflow-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Doc type</Label>
              <Input required data-testid="workflow-doc-type-input" value={form.doc_type} onChange={set("doc_type")} className="mt-1.5" placeholder="PO" />
              <FieldError error={errors.doc_type} />
            </div>
            <div>
              <Label>Name</Label>
              <Input required data-testid="workflow-name-input" value={form.name} onChange={set("name")} className="mt-1.5" placeholder="PO Approval" />
              <FieldError error={errors.name} />
            </div>
          </div>

          <div>
            <Label>Steps (in order)</Label>
            <div className="mt-1.5 space-y-2">
              {steps.map((s, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`workflow-step-row-${idx}`}>
                  <span className="w-5 shrink-0 font-mono text-xs text-muted-foreground">{idx + 1}.</span>
                  <Input
                    required
                    placeholder="Step name"
                    value={s.name}
                    onChange={(e) => updateStep(idx, "name", e.target.value)}
                    data-testid={`workflow-step-name-input-${idx}`}
                  />
                  <Select value={s.approver_role} onValueChange={(v) => updateStep(idx, "approver_role", v)}>
                    <SelectTrigger className="w-40 shrink-0" data-testid={`workflow-step-role-select-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ERP_APPROVER_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                  {steps.length > 1 && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeStep(idx)} data-testid={`remove-workflow-step-button-${idx}`}>
                      <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={addStep} data-testid="add-workflow-step-button">
              <Plus className="h-3.5 w-3.5" /> Add step
            </Button>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-workflow-submit-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
